import { useEffect, useState } from 'react';
import { planService, type PlanDetail, type PlanLimits } from '@/services/planService';

/**
 * The caller's plan + limits, fetched once from GET /api/Plan/mine (which resolves
 * from the JWT claim, so the UI can't disagree with the API about what the user
 * can do). The first client-side capability gate — reuse this hook rather than
 * inventing another fetch pattern.
 *
 * Fails OPEN: on a fetch error `limits` stays null and callers should render the
 * feature — the backend capability checks are authoritative, and locking a paying
 * user out of a real-money screen over a transient error is the worse failure.
 */
export function usePlanLimits(enabled = true): { plan: PlanDetail | null; limits: PlanLimits | null; loading: boolean } {
  const [plan, setPlan] = useState<PlanDetail | null>(null);
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    planService.mine()
      .then((p) => { if (alive) setPlan(p); })
      .catch(() => { /* fail open — see docstring */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [enabled]);

  return { plan, limits: plan?.limits ?? null, loading };
}

export default usePlanLimits;
